import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  FormControl,
  FormHelperText,
  FormLabel,
  OutlinedInput,
  Stack,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTranslation } from "react-i18next";
import type {
  ContactState,
  HandleState,
} from "../../../../../core/models/managementsUpdate/managementsUpdateContact/managementsUpdateContact.model";
import LoadingComponentComponent from "../../../../UI/LoadingComponentComponent/LoadingComponentComponent";
import { isNull } from "lodash";
import { useCallback } from "react";

interface UpdateContactCardProps {
  cup: ContactState;
  defaultExpanded: boolean;
  handleUpdateContact: HandleState;
  handleValidateInput: HandleState;
  cupIndex: number;
  isDataLoaded: boolean;
  onChange: () => void;
}

const UpdateContactCard: React.FC<UpdateContactCardProps> = ({
  cup,
  defaultExpanded,
  handleUpdateContact,
  cupIndex,
  handleValidateInput,
  isDataLoaded,
  onChange,
}) => {
  const { t } = useTranslation();

  const classIcon =
    cup.envirorment.toLowerCase() === "luz"
      ? "t-icon-electricite-big"
      : "t-icon-gaz";

  const getPhoneErrorText = useCallback(
    (error: ContactState["phone"][number]["error"]) => {
      if (isNull(error)) {
        return "";
      } else {
        switch (error) {
          case "format":
            return t("Gestiones.Formato de teléfono incorrecto");
          case "repetition":
            return t("Gestiones.Errores.Teléfono repetido");
          default:
            return "";
        }
      }
    },
    [t]
  );

  return (
    <Accordion
      disableGutters
      defaultExpanded={defaultExpanded}
      className="accordion__container"
      onChange={onChange}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon color="primary" />}
        className="accordion__summary"
      >
        <Stack gap={1} display="flex" direction={"row"} alignItems={"center"}>
          <span className="t-icon-map brand-red accordion__icon--medium accordion__icon--desktop-only" />
          <Stack>
            <Typography
              variant="body2"
              component="h3"
              sx={{
                color: "gray.900",
              }}
            >
              {cup.supplyAddress}
            </Typography>
            <Stack
              display={"flex"}
              direction={"row"}
              gap={1}
              alignItems={"center"}
            >
              <span className={`${classIcon} accordion__icon--environment`} />
              <Typography
                variant="body2"
                component="h3"
                className="accordion__summary-subtitle"
              >
                {cup.cupsCode}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          padding: { xs: "24px 16px", md: "48px" },
          borderTop: "1px solid #EFF3F5",
        }}
      >
        <Stack
          gap={4}
          display={"flex"}
          flexDirection={{ xs: "column", md: "row" }}
          width={"100%"}
        >
          {isDataLoaded ? (
            <>
              <Stack display={"flex"} gap={1} width={"100%"}>
                <Label>{t(`Gestiones.Correos`)}</Label>
                {cup.email.map((email, i) => (
                  <Stack
                    key={`email-${i}`}
                    display={"flex"}
                    direction={"row"}
                    alignItems={"center"}
                    gap={{
                      xs: 1,
                      md: 2,
                    }}
                    width={"100%"}
                  >
                    <FormLabel
                      sx={{
                        textWrap: "nowrap",
                        fontSize: "1rem",
                      }}
                    >{`${i + 1} -`}</FormLabel>
                    <FormControl
                      key={i}
                      required={i === 0}
                      sx={{ width: "100%" }}
                    >
                      <OutlinedInput
                        type={"email"}
                        value={email.value}
                        onChange={(e) =>
                          handleUpdateContact(e, cupIndex, "email", i)
                        }
                        onBlur={(e) =>
                          handleValidateInput(e, cupIndex, "email", i)
                        }
                        fullWidth
                        sx={{ fontSize: "1rem", height: "48px" }}
                      />

                      {!isNull(email.error) && (
                        <FormHelperText
                          error={!isNull(email.error)}
                          sx={{ marginLeft: 0 }}
                        >
                          {!isNull(email.error) &&
                            t("Gestiones.Formato de correo incorrecto")}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Stack>
                ))}
              </Stack>
              <Stack display={"flex"} gap={1} width={"100%"}>
                <Label>{t(`Gestiones.Teléfonos`)}</Label>
                {cup.phone.map((phone, i) => (
                  <Stack
                    key={`phone-${i}`}
                    display={"flex"}
                    direction={"row"}
                    alignItems={"center"}
                    gap={{
                      xs: 1,
                      md: 2,
                    }}
                    width={"100%"}
                  >
                    <FormLabel
                      sx={{
                        textWrap: "nowrap",
                        fontSize: "1rem",
                      }}
                    >{`${i + 1} -`}</FormLabel>
                    <FormControl
                      key={i}
                      required={i === 0}
                      sx={{ width: "100%" }}
                      error={!isNull(phone.error) && phone.value.trim() !== ""}
                    >
                      <OutlinedInput
                        key={i}
                        type={"tel"}
                        value={phone.value}
                        onChange={(e) =>
                          handleUpdateContact(e, cupIndex, "phone", i)
                        }
                        onBlur={(e) =>
                          handleValidateInput(e, cupIndex, "phone", i)
                        }
                        fullWidth
                        sx={{ fontSize: "1rem", height: "48px" }}
                      />
                      {!isNull(phone.error) && phone.value.trim() !== "" && (
                        <FormHelperText
                          error={!isNull(phone.error) && phone.value.trim() !== ""}
                          sx={{ marginLeft: 0 }}
                        >
                          {getPhoneErrorText(phone.error)}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Stack>
                ))}
              </Stack>
            </>
          ) : (
            <LoadingComponentComponent
              isLoading={true}
              position={"relative"}
              height={"303px"}
            />
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

const Label = ({ children }: { children: React.ReactNode }) => (
  <FormLabel
    sx={{
      fontSize: "1rem",
      fontWeight: "600",
      marginLeft: { xs: "24px", md: "34px" },
    }}
  >
    {children}
  </FormLabel>
);

export default UpdateContactCard;
